/**
 * Customer Notification Service
 *
 * API client for in-app notification center.
 * Uses Services API with CUSTOMERS Cognito JWT auth.
 */

import { getIdToken } from '../lib/auth';

const SERVICES_API_URL = import.meta.env.VITE_SERVICES_API_URL ?? import.meta.env.VITE_API_URL ?? '';

// =============================================================================
// Types
// =============================================================================

export type NotificationPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';

export interface CustomerNotification {
  customerId: string;
  notificationId: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  priority: NotificationPriority;
  actionUrl?: string;
  metadata?: Record<string, string>;
  channel: string;
  createdAt: string;
  expiresAt?: number;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
  meta?: {
    nextToken?: string;
    unreadCount?: number;
  };
}

// =============================================================================
// API Helper
// =============================================================================

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const token = await getIdToken();

  const response = await fetch(`${SERVICES_API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
      'x-tenant-id': 'geauxfile',
      ...options.headers,
    },
  });

  const data = (await response.json()) as ApiResponse<T>;

  if (!response.ok) {
    throw new Error(data.error?.message ?? `API error: ${response.status}`);
  }

  return data;
}

// =============================================================================
// Service Functions
// =============================================================================

export async function getNotifications(options?: {
  limit?: number;
  nextToken?: string;
  unreadOnly?: boolean;
}): Promise<{
  notifications: CustomerNotification[];
  nextToken?: string;
  unreadCount: number;
}> {
  if (!SERVICES_API_URL) throw new Error('Notifications unavailable: API not configured');

  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.nextToken) params.set('nextToken', options.nextToken);
  if (options?.unreadOnly) params.set('unreadOnly', 'true');

  const response = await apiRequest<CustomerNotification[]>(`/notifications?${params}`);

  return {
    notifications: response.data ?? [],
    nextToken: response.meta?.nextToken,
    unreadCount: response.meta?.unreadCount ?? 0,
  };
}

export async function getUnreadCount(): Promise<number> {
  if (!SERVICES_API_URL) return 0;

  try {
    const response = await apiRequest<{ unreadCount: number }>('/notifications/unread-count');
    return response.data?.unreadCount ?? 0;
  } catch {
    return 0;
  }
}

export async function markAsRead(notificationId: string): Promise<void> {
  if (!SERVICES_API_URL) return;

  await apiRequest(`/notifications/${notificationId}/read`, { method: 'PATCH' });
}

export async function markAllAsRead(): Promise<void> {
  if (!SERVICES_API_URL) return;

  await apiRequest('/notifications/read-all', { method: 'POST' });
}
